'use client';

import { c as _c } from "react/compiler-runtime";
import { cn } from '@core/utils';
import { autoUpdate, flip, offset, shift, size, useFloating } from '@floating-ui/react';
import { Slot } from '@radix-ui/react-slot';
import * as RadixTooltip from '@radix-ui/react-tooltip';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import SvgCircleClose from '../../../__generated__/icon/SvgCircleClose';
interface TooltipProps {
  children: ReactNode;
  open?: boolean;
  onOpenChange?: (value: boolean) => void;
  onContentClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  defaultOpen?: boolean;
  floatingPadding?: number;
  delayDuration?: number;
  asChild?: boolean;
  label: React.ReactNode;
  className?: string;
  triggerClassName?: string;
  contentProps?: RadixTooltip.TooltipContentProps;
  isArrow?: boolean;
  /**
   * 툴팁이 표시될 방향을 지정합니다.
   * 'top': 트리거 요소 위에 툴팁 표시
   * 'bottom': 트리거 요소 아래에 툴팁 표시
   */
  side?: 'top' | 'bottom';
  /**
   * 툴팁 화살표를 기준으로 콘텐츠 박스의 정렬 방향을 지정합니다.
   * 'start': 왼쪽 정렬
   * 'center': 가운데 정렬 (기본값)
   * 'end': 오른쪽 정렬
   */
  align?: 'start' | 'center' | 'end';
  /**
   * 툴팁의 테마를 지정합니다.
   * 'dark': 어두운 테마 (기본값)
   * 'light': 밝은 테마
   * 'outlined': 흰색 배경 + 테두리 + 블러 스타일
   */
  theme?: 'dark' | 'light' | 'outlined';
  /**
   * 제목을 표시합니다.
   */
  title?: string;
  /**
   * X버튼을 표시할지 여부를 지정합니다.
   */
  showCloseButton?: boolean;
  /**
   * X버튼 클릭 시 호출되는 콜백 함수입니다.
   */
  onClose?: () => void;
}
export const Tooltip = ({
  children,
  defaultOpen = false,
  open,
  onOpenChange,
  onContentClick,
  label,
  floatingPadding = 10,
  asChild,
  className,
  delayDuration = 200,
  triggerClassName,
  contentProps,
  isArrow = false,
  side,
  align,
  theme = 'dark',
  title,
  showCloseButton = false,
  onClose
}: TooltipProps) => {
  const [localOpened, setLocalOpened] = useState(defaultOpen);
  const isOpened = open ?? localOpened;
  const [maxHeight, setMaxHeight] = useState<number>();
  const {
    context
  } = useFloating({
    onOpenChange: (value: boolean) => {
      setLocalOpened(value);
      onOpenChange?.(value);
    },
    open: isOpened,
    middleware: [offset(floatingPadding), flip(), shift({
      padding: floatingPadding
    }), size({
      apply({
        availableHeight
      }) {
        flushSync(() => {
          setMaxHeight(availableHeight - floatingPadding);
        });
      }
    })],
    whileElementsMounted: autoUpdate
  });
  context.floatingStyles.maxHeight = maxHeight;
  const Button = asChild ? Slot : 'button';
  const toggle = () => {
    setLocalOpened(prev => !prev);
  };
  const handleClose = () => {
    setLocalOpened(false);
    onOpenChange?.(false);
    onClose?.();
  };

  // ESC 키로 닫기 (showCloseButton이 true일 때만)
  useEffect(() => {
    if (!showCloseButton || !isOpened) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCloseButton, isOpened]);
  const isLightTheme = theme === 'light';
  const isOutlined = theme === 'outlined';
  const isDialogMode = showCloseButton;
  return <RadixTooltip.Provider delayDuration={isDialogMode ? 0 : delayDuration}>
      <RadixTooltip.Root defaultOpen={defaultOpen} delayDuration={0} open={open ?? isOpened} onOpenChange={toggle}>
        <RadixTooltip.Trigger asChild onClick={toggle}>
          <Button className={triggerClassName}>{children}</Button>
        </RadixTooltip.Trigger>
        <RadixTooltip.Portal>
          <RadixTooltip.Content className={cn('overflow-auto whitespace-pre-wrap break-all', 'z-30 max-w-280 font-400 typo-caption1', isOutlined ? 'rounded-small border border-Gray-300 bg-Gray-white/[0.98] px-16 py-12 text-Gray-700 shadow-lg backdrop-blur-[5px]' : cn('rounded-xsmall', isDialogMode ? 'p-20' : 'p-12', isLightTheme ? 'bg-Gray-white text-Gray-900 shadow-lg' : 'bg-Gray-black/70 text-Gray-white', isDialogMode && isLightTheme && 'shadow-xl'), className)} {...contentProps} sideOffset={floatingPadding} side={side} align={align} alignOffset={-10} onClick={onContentClick} onPointerDownOutside={isDialogMode ? handleClose : undefined} onEscapeKeyDown={isDialogMode ? handleClose : undefined}>
            {(title || showCloseButton) && <div className="mb-8 flex items-center justify-between">
                <div className="font-700 text-Gray-900 typo-body4">{title}</div>
                {showCloseButton && <button onClick={handleClose} className="flex size-20 items-center justify-center rounded-pill bg-Gray-50 hover:bg-Gray-100">
                    <SvgCircleClose className="size-20" />
                  </button>}
              </div>}
            <div>{label}</div>
            {isArrow && <RadixTooltip.Arrow className={cn('opacity-70', isLightTheme || isOutlined ? 'fill-Gray-white' : 'fill-Gray-black/70')} width={14} height={8} />}
          </RadixTooltip.Content>
        </RadixTooltip.Portal>
      </RadixTooltip.Root>
    </RadixTooltip.Provider>;
};
interface TooltipProviderProps {
  children: ReactNode;
  delayDuration?: number;
}
export const TooltipProvider = t0 => {
  const $ = _c(3);
  const {
    children,
    delayDuration: t1
  } = t0;
  const delayDuration = t1 === undefined ? 200 : t1;
  let t2;
  if ($[0] !== children || $[1] !== delayDuration) {
    t2 = <RadixTooltip.Provider delayDuration={delayDuration}>{children}</RadixTooltip.Provider>;
    $[0] = children;
    $[1] = delayDuration;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};
export const TooltipRoot = ({
  children,
  defaultOpen = false,
  onOpenChange,
  label,
  floatingPadding = 10,
  asChild,
  className,
  triggerClassName,
  theme = 'dark',
  title,
  showCloseButton = false,
  onClose
}: TooltipProps) => {
  const [isOpened, setIsOpened] = useState(defaultOpen);
  const [maxHeight, setMaxHeight] = useState<number>();
  const {
    context
  } = useFloating({
    onOpenChange: (value: boolean) => {
      setIsOpened(value);
      onOpenChange?.(value);
    },
    open: isOpened,
    middleware: [offset(floatingPadding), flip(), shift({
      padding: floatingPadding
    }), size({
      apply({
        availableHeight
      }) {
        flushSync(() => {
          setMaxHeight(availableHeight - floatingPadding);
        });
      }
    })],
    whileElementsMounted: autoUpdate
  });
  context.floatingStyles.maxHeight = maxHeight;
  const Button = asChild ? Slot : 'button';
  const toggle = () => {
    setIsOpened(prev => !prev);
  };
  const handleClose = () => {
    setIsOpened(false);
    onOpenChange?.(false);
    onClose?.();
  };

  // ESC 키로 닫기 (showCloseButton이 true일 때만)
  useEffect(() => {
    if (!showCloseButton || !isOpened) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCloseButton, isOpened]);
  const isLightTheme = theme === 'light';
  const isOutlined = theme === 'outlined';
  const isDialogMode = showCloseButton;
  return <RadixTooltip.Root defaultOpen={defaultOpen} delayDuration={0} open={isOpened} onOpenChange={toggle}>
      <RadixTooltip.Trigger asChild onClick={toggle}>
        <Button className={triggerClassName}>{children}</Button>
      </RadixTooltip.Trigger>
      <RadixTooltip.Portal>
        <RadixTooltip.Content className={cn('overflow-auto whitespace-pre-wrap break-all', 'z-30 max-w-180 font-400 typo-caption1', isOutlined ? 'rounded-small border border-Gray-300 bg-Gray-white/[0.98] px-16 py-12 text-Gray-700 shadow-lg backdrop-blur-[5px]' : cn('rounded-xsmall', isDialogMode ? 'p-20' : 'p-12', isLightTheme ? 'bg-Gray-white text-Gray-900 shadow-lg' : 'bg-Gray-black/70 text-Gray-white', isDialogMode && isLightTheme && 'shadow-xl'), className)}>
          {(title || showCloseButton) && <div className="mb-8 flex items-center justify-between">
              {title && <div className="text-14 font-700 text-Gray-900">{title}</div>}
              {showCloseButton && <button onClick={handleClose} className="flex size-20 items-center justify-center rounded-pill hover:bg-Gray-100">
                  <SvgCircleClose className="size-20" />
                </button>}
            </div>}
          <div>{label}</div>
        </RadixTooltip.Content>
      </RadixTooltip.Portal>
    </RadixTooltip.Root>;
};